// src/components/PieChart.js
import { useRef } from "react";
import { Chart as Chartjs, registerables } from 'chart.js';
import { Bar, Chart, Doughnut } from "react-chartjs-2";
import { getRelativePosition } from 'chart.js/helpers';
import PropTypes from "prop-types";
import moment from "moment";
import 'chartjs-adapter-moment';
import zoomPlugin from "chartjs-plugin-zoom";
import data from "components/Maps/HeatMap/data";

Chartjs.register(...registerables);
Chartjs.register(zoomPlugin); // REGISTER PLUGIN

const centerTextPlugin = {
    id: 'centerText',
    beforeDraw: function (chart) {
        if (chart.config.options.elements.center) {
            const { ctx, chartArea: { top, left, width, height } } = chart;
            const centerConfig = chart.config.options.elements.center;

            ctx.save();

            // 1. Calculate Font Sizes based on height
            const mainFontSize = (height / 100).toFixed(2);
            const subFontSize = (mainFontSize * 0.3).toFixed(2); // Subtext is 40% of main text size

            const text = centerConfig.text || "";
            const subtext = centerConfig.subtext || "";

            // 2. Draw Main Text (Top Line)
            ctx.font = `bold ${mainFontSize}em sans-serif`;
            ctx.textBaseline = "middle";
            ctx.textAlign = "center";
            ctx.fillStyle = centerConfig.color || "#000";

            // Calculate Center
            const centerX = left + width / 2;
            const centerY = top + height / 2;

            // If there's subtext, offset the main text upward
            const yOffset = subtext ? (mainFontSize * 6) : 0;

            ctx.fillText(text, centerX, centerY - (subtext ? yOffset : 0));

            // 3. Draw Subtext (Bottom Line)
            if (subtext) {
                ctx.font = `${subFontSize}em sans-serif`;
                ctx.fillStyle = centerConfig.subtextColor || "#67748e";

                // Wrap subtext if it's too long
                const maxWidth = width * 0.6; // Use 60% of chart width as max wrap width
                const words = subtext.split(" ");
                let line = "";
                let testLine = "";
                let lineCount = 0;
                const lineHeight = subFontSize * 15; // Rough estimate of line height

                for (let n = 0; n < words.length; n++) {
                    testLine = line + words[n] + " ";
                    let metrics = ctx.measureText(testLine);
                    let testWidth = metrics.width;
                    if (testWidth > maxWidth && n > 0) {
                        ctx.fillText(line, centerX, centerY + yOffset + (lineCount * lineHeight));
                        line = words[n] + " ";
                        lineCount++;
                    } else {
                        line = testLine;
                    }
                }
                ctx.fillText(line, centerX, centerY + yOffset + (lineCount * lineHeight));
            }

            ctx.restore();
        }
    },
};

function DoughnutChart({ chartData = [], totalCount, height = '100%', maxHeight = '100%', startTime, endTime, groupSize, setStartTime, setEndTime }) {
    const chartRef = useRef(null);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%', // Adjust this to make the "hole" larger or smaller
        elements: {
            center: {
                text: totalCount.toFixed(2),
                subtext: 'Infringements per day',
                color: '#344767', // Text color
            },
        },
    };

    var chart = (
        <Doughnut
            ref={chartRef}
            data={chartData}
            options={options}
            plugins={[centerTextPlugin]}
            style={{minHeight: '200px', width: 'auto'}}
        />
    )

    return chart;
};

// Typechecking props for the DoughnutChart
DoughnutChart.propTypes = {
    chartData: PropTypes.object,
    totalCount: PropTypes.number,
    startTime: PropTypes.instanceOf(moment()),
    setStartTime: PropTypes.func,
    endTime: PropTypes.instanceOf(moment()),
    setEndTime: PropTypes.func,
    groupSize: PropTypes.number,
};


export default DoughnutChart;